import { useState } from 'react'
import { toast } from 'sonner'
import { Eye, EyeOff, Save, Settings2 } from 'lucide-react'
import { Link } from 'react-router-dom'
import { useAppSettings, useUpdateAppSetting } from '@/hooks/useApi'
import type { AppSetting } from '@/api/client'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { GscConnection } from '@/components/settings/GscConnection'

const CATEGORY_LABELS: Record<string, string> = {
  google_search_console: 'Google Search Console',
  seo_scanner: 'SEO-scanner',
  notificaties: 'Notificaties',
  drempels: 'Drempels & schema\'s',
  beveiliging: 'Beveiliging',
  banking: 'Banking',
  // Kept so a row with an unmigrated category still gets a readable heading.
  seo: 'SEO',
  monitoring: 'Monitoring',
}

const CATEGORY_ORDER = [
  'google_search_console',
  'seo_scanner',
  'notificaties',
  'drempels',
  'beveiliging',
  'banking',
  'seo',
  'monitoring',
]

interface SettingRowProps {
  setting: AppSetting
}

function SettingRow({ setting }: SettingRowProps) {
  const [editing, setEditing] = useState(false)
  const [inputValue, setInputValue] = useState(setting.value ?? '')
  const [showSecret, setShowSecret] = useState(false)
  const updateSetting = useUpdateAppSetting()

  const hasValue = Boolean(setting.value)

  const handleSave = async () => {
    try {
      await updateSetting.mutateAsync({
        key: setting.key,
        value: inputValue.trim() || null,
      })
      setEditing(false)
      setShowSecret(false)
      toast.success(`${setting.label} opgeslagen`)
    } catch (err) {
      toast.error('Opslaan mislukt', {
        description: err instanceof Error ? err.message : 'Onbekende fout',
      })
    }
  }

  const handleCancel = () => {
    setInputValue(setting.value ?? '')
    setEditing(false)
    setShowSecret(false)
  }

  return (
    <div className="rounded-lg border p-4 space-y-3">
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <span className="font-medium text-sm">{setting.label}</span>
            {hasValue ? (
              <Badge className="bg-green-600 text-white text-xs">Ingesteld</Badge>
            ) : (
              <Badge variant="outline" className="text-xs text-muted-foreground">
                Niet ingesteld
              </Badge>
            )}
          </div>
          {setting.description && (
            <p className="mt-1 text-xs text-muted-foreground leading-relaxed">
              {setting.description}
            </p>
          )}
          <p className="mt-1 text-xs font-mono text-muted-foreground">{setting.key}</p>
        </div>
        {!editing && (
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              setInputValue(setting.value ?? '')
              setEditing(true)
            }}
            className="shrink-0"
          >
            {hasValue ? 'Wijzigen' : 'Instellen'}
          </Button>
        )}
      </div>

      {editing && (
        <div className="flex items-center gap-2">
          <div className="relative flex-1">
            <Input
              type={setting.is_secret && !showSecret ? 'password' : 'text'}
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleSave()
                if (e.key === 'Escape') handleCancel()
              }}
              placeholder={setting.is_secret ? '••••••••••••••••' : `Voer ${setting.label} in`}
              autoFocus
              className="pr-10"
            />
            {setting.is_secret && (
              <button
                type="button"
                onClick={() => setShowSecret(!showSecret)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                tabIndex={-1}
              >
                {showSecret ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            )}
          </div>
          <Button
            size="sm"
            onClick={handleSave}
            disabled={updateSetting.isPending}
          >
            <Save className="h-4 w-4 mr-1" />
            Opslaan
          </Button>
          <Button variant="outline" size="sm" onClick={handleCancel}>
            Annuleer
          </Button>
        </div>
      )}
    </div>
  )
}

export default function Settings() {
  const { data: appSettings, isLoading: settingsLoading } = useAppSettings()

  // Group settings by category
  const byCategory: Record<string, AppSetting[]> = {}
  if (appSettings) {
    for (const s of appSettings) {
      if (!byCategory[s.category]) byCategory[s.category] = []
      byCategory[s.category].push(s)
    }
  }
  const sortedCategories = Object.keys(byCategory).sort(
    (a, b) =>
      (CATEGORY_ORDER.indexOf(a) === -1 ? 99 : CATEGORY_ORDER.indexOf(a)) -
      (CATEGORY_ORDER.indexOf(b) === -1 ? 99 : CATEGORY_ORDER.indexOf(b)),
  )

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-3xl font-bold">Instellingen</h1>
        <p className="text-muted-foreground mt-1">
          API keys en configuratie beheren. Waarden worden opgeslagen in de database en
          overschrijven de omgevingsvariabelen.
        </p>
      </div>

      {/* Account-level GSC connection. The property per dossier is set on the
          dossier itself; this is the one Google account behind all of them. */}
      <GscConnection />

      {/* Editable settings grouped by category */}
      {settingsLoading ? (
        <Card>
          <CardContent className="p-8 text-center text-sm text-muted-foreground">
            Instellingen laden...
          </CardContent>
        </Card>
      ) : (
        sortedCategories.map((cat) => (
          <Card key={cat}>
            <CardHeader>
              <div className="flex items-center gap-2">
                <Settings2 className="h-4 w-4 text-muted-foreground" />
                <CardTitle>{CATEGORY_LABELS[cat] ?? cat}</CardTitle>
              </div>
            </CardHeader>
            <CardContent className="space-y-3">
              {byCategory[cat].map((s) => (
                <SettingRow key={s.key} setting={s} />
              ))}
            </CardContent>
          </Card>
        ))
      )}

      <p className="text-sm text-muted-foreground">
        Systeemstatus, container-images, het takenschema en de onderhoudslog
        staan op <Link to="/system" className="text-primary underline">Systeem</Link>.
      </p>

    </div>
  )
}
